import { type FC, type MutableRefObject, type PropsWithChildren } from 'react'
import { InputMask } from '@react-input/mask'
import type { InputProps } from 'antd/es/input'
import Group from 'antd/es/input/Group'
import Search from 'antd/es/input/Search'
import TextArea from 'antd/es/input/TextArea'
import Password from 'antd/es/input/Password'

import { E_Colors } from '@/helpers/colors'
import { formatAnyInputToPhoneMask, phoneMask, dateMask, extractPhoneDigits } from './helpers'
import { InputWrapper } from './input-wrapper'
import {
  SC_InputAndLabelWrapper,
  SC_InputWrapper,
} from './styled'


interface T_CompoundedComponent {
  Group: typeof Group
  Search: typeof Search
  TextArea: typeof TextArea
  Password: typeof Password
}

export type T_FrameStyle = 'stroked' | 'framed'
export type T_CustomInputType = 'numeric'

export type T_InputProps = InputProps & {
  label?: string,
  $frameStyle?: T_FrameStyle
  $minWidth?: string
  setFormValue?: (key: string, value: string) => void
  $customType?: T_CustomInputType
  isSearch?: boolean
  $isPhone?: boolean
  $isDateMask?: boolean
  $extraSize?: 'veryLarge'
  $isVeryRadiused?: boolean
  $isWhite?: boolean
  $ref?: MutableRefObject<HTMLElement | null>
  setFunction?: (value: any) => void
}

export const Input: FC<PropsWithChildren<T_InputProps>> & T_CompoundedComponent = (props) => {
  const {
    label,
    name,
    value,
    suffix,
    $minWidth = '',
    setFormValue,
    $frameStyle = 'stroked',
    $customType,
    disabled = false,
    onChange,
    placeholder,
    $isPhone = false,
    $isDateMask = false,
    $extraSize,
    $isVeryRadiused = false,
    $isWhite = false,
    $ref,
    setFunction,
    ...restProps
  } = props


  return (
    <SC_InputAndLabelWrapper
      disabled={disabled}
      $frameStyle={$frameStyle}
      $minWidth={$minWidth}
      $customType={$customType}
      $extraSize={$extraSize}
      $isVeryRadiused={$isVeryRadiused}
      $isWhite={$isWhite}
    >
      {label && (
        <label htmlFor={name}>
          {label}
        </label>
      )}

      <SC_InputWrapper>
        {$isPhone ? (
          <InputMask
            name={name}
            data-input-phone
            value={value as any}
            onChange={onChange}
            onInput={onChange}
            disabled={disabled}
            onPaste={(e) => {
              e.preventDefault()
              const pastedText = e.clipboardData.getData('text/plain')
              setFunction?.(formatAnyInputToPhoneMask(pastedText))
            }}
            placeholder={placeholder || ''}
            showMask
            mask={phoneMask}
            replacement={{ _: /\d/ }}
            track={({ inputType, data }) => {
              if (inputType === 'insert' && data) {
                return extractPhoneDigits(data)
              }

              return data
            }}
            style={{
              height: $extraSize === 'veryLarge' ? '60px' : 'auto',
              borderRadius: $isVeryRadiused ? '24px' : 0,
              color: value === phoneMask ? E_Colors.Gray66 : E_Colors.Black,
            }}
          />
        ) : $isDateMask ? (
          <InputMask
            name={name}
            data-input-date
            value={value as any}
            onChange={onChange}
            disabled={disabled}
            placeholder={placeholder || ''}
            showMask={!!(/\d/.test(value as string))}
            mask={dateMask}
            replacement={{ _: /\d/ }}
            style={{
              height: $extraSize === 'veryLarge' ? '60px' : 'auto',
              borderRadius: $isVeryRadiused ? '24px' : 0,
              color: value === dateMask ? E_Colors.GrayB2 : E_Colors.TextPrimary,
            }}
          />
        ) : (
          <InputWrapper
            name={name}
            value={value}
            placeholder={placeholder}
            $frameStyle={$frameStyle}
            $customType={$customType}
            disabled={disabled}
            onChange={onChange}
            setFormValue={setFormValue}
            $extraSize={$extraSize}
            $isVeryRadiused={$isVeryRadiused}
            $ref={$ref}
            {...restProps}
          />
        )}

        {suffix || null}
      </SC_InputWrapper>
    </SC_InputAndLabelWrapper>
  )
}

Input.Group = Group
Input.Search = Search
Input.Password = Password
Input.TextArea = TextArea
